import { Component, Prop, State, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-troubleshooting';

/**
 * Troubleshooting Q&A block for resolving common SignNow issues.
 * Two-column split: heading + intro text on the left (5/12), expandable accordion of question/answer items on the right (7/12).
 * Uses interactive multi-open accordion behavior (all items open by default).
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for FAQ and troubleshooting for {keyword}. 6-10 words.
 * @slot text - {content} <me-text> - Introductory paragraph for the FAQ section. You MUST write 15-30 words.
 * @example
 *   <sn-troubleshooting
 *     items='[{"title":"Document will not upload","text":"Check that your file is under 25 MB and in PDF, DOC, or DOCX format."},{"title":"Signer did not receive email","text":"Ask the signer to check their spam folder and whitelist noreply@signnow.com."}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Troubleshooting</me-heading>
 *     <me-text slot="text">Common issues and their solutions.</me-text>
 *   </sn-troubleshooting>
 */
@Component({
  tag: 'sn-troubleshooting',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcTroubleshooting {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Accordion Q&A items.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Question-style label for this troubleshooting item. 5-15 words.
   * @item.property {string} text - Clear factual answer referencing real signNow features or compliance standards. You MUST write 30-50 words.
   * @item.required title
   * @item.required text
   * @item.content title text
   * @minItems 6
   * @maxItems 6
   */
  @Prop({ mutable: true }) items?: { title?: string; text?: string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `items` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} text - Fallback body text (use slot="text" instead)
   * @property {array} items - Fallback accordion items (use items prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        text?: string;
        raw_html?: string;
        items?: { title?: string; text?: string }[];
      }
    | string;

  @State() openIndices: Set<number> = new Set();

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.items = parseJsonProp(this.items);

    // Open all items by default.
    const list = this.resolveItems();
    this.openIndices = new Set(list.map((_, i) => i));
  }

  private resolveItems(): { title?: string; text?: string }[] {
    if (Array.isArray(this.items)) return this.items;

    const values = typeof this.values === 'object' ? this.values : undefined;
    if (Array.isArray(values?.items)) return values.items;

    return [];
  }

  private toggle(index: number) {
    const next = new Set(this.openIndices);
    if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    this.openIndices = next;
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const items = this.resolveItems();

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <me-row direction="column" directionLg="row">
          <me-column spanLg="5">
            <div class={`${COMPONENT_NAME}__left`}>
              <slot name="heading">
                {values?.title && (
                  <me-heading variant="4" tag="h4">
                    {values.title}
                  </me-heading>
                )}
              </slot>
              <slot name="text">{values?.text && <me-text>{values.text}</me-text>}</slot>
            </div>
          </me-column>
          {items.length > 0 && (
            <me-column class={`${COMPONENT_NAME}__list`} spanLg="7">
              <div class={`${COMPONENT_NAME}__right`}>
                {items.map((item, index) => {
                  const isOpen = this.openIndices.has(index);
                  return (
                    <div class={`${COMPONENT_NAME}__accordion ${isOpen ? `${COMPONENT_NAME}__accordion--open` : ''}`}>
                      <h3 class={`${COMPONENT_NAME}__question`}>
                        <button class={`${COMPONENT_NAME}__trigger`} type="button" aria-expanded={String(isOpen)} onClick={() => this.toggle(index)}>
                          <me-text tag="span" weight="semi-bold">
                            {item.title}
                          </me-text>
                          <span class={`${COMPONENT_NAME}__icon`} aria-hidden="true"></span>
                        </button>
                      </h3>
                      {isOpen && item.text && (
                        <div class={`${COMPONENT_NAME}__panel`}>
                          <me-text class={`${COMPONENT_NAME}__panel-text`}>{item.text}</me-text>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </me-column>
          )}
        </me-row>
      </me-section>
    );
  }
}
